/**
 * RouteErrorFallback — KAN-374
 * ============================
 *
 * Per-route fallback UI shown when a Sentry.ErrorBoundary inside a Route
 * element catches a render-time exception. Distinct from the app-root
 * ErrorBoundary fallback so a single-page React crash doesn't take the
 * whole SPA down — the user can still navigate to other tabs via the
 * BottomTabBar / header nav.
 *
 * The route name is mandatory (no `unknown` literals — see
 * `feedback_telemetry_everywhere`) so Sentry events can be filtered by
 * `tags.route` and the user always sees which surface failed.
 */

import React from 'react';
import { ArrowPathIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';

interface RouteErrorFallbackProps {
  /** Stable route identifier — e.g. 'home', 'track', 'performance', 'profile'. */
  route: string;
  /** When provided, called by the Try Again button. Sentry's ErrorBoundary
   *  passes `resetError` here. */
  resetError?: () => void;
  /** When provided, gets the underlying Error object for display in dev. */
  error?: unknown;
}

export const RouteErrorFallback: React.FC<RouteErrorFallbackProps> = ({
  route,
  resetError,
  error,
}) => {
  const isDev = import.meta.env.DEV;
  return (
    <div
      data-testid={`route-error-fallback-${route}`}
      role="alert"
      className="min-h-[60vh] flex items-center justify-center px-4 py-12"
    >
      <div className="max-w-md w-full bg-surface-elevated rounded-xl border border-red-500/20 p-6">
        <div className="flex items-center mb-3">
          <ExclamationTriangleIcon className="w-6 h-6 text-red-400 mr-2" />
          <h2 className="text-lg font-semibold text-white">
            This page hit a snag
          </h2>
        </div>
        <p className="text-sm text-white/70 mb-4">
          The {route} tab couldn&apos;t render. The rest of the app is still
          fine — try refreshing this page or jumping to another tab.
        </p>
        {isDev && error instanceof Error && (
          <details className="mb-4">
            <summary className="cursor-pointer text-xs text-white/50 hover:text-white/70">
              Error details (dev only)
            </summary>
            <pre className="mt-2 p-2 bg-black/40 rounded text-[11px] text-red-300 overflow-auto max-h-40">
              {error.message}
              {error.stack ? `\n\n${error.stack}` : ''}
            </pre>
          </details>
        )}
        <div className="flex gap-2">
          {resetError && (
            <button
              onClick={resetError}
              data-testid={`route-error-retry-${route}`}
              className="flex-1 inline-flex items-center justify-center gap-2 px-3 py-2 bg-brand hover:bg-brand/90 text-zinc-950 rounded-lg text-sm font-medium transition-colors"
            >
              <ArrowPathIcon className="w-4 h-4" />
              Try Again
            </button>
          )}
          <button
            onClick={() => (window.location.href = '/')}
            className="flex-1 px-3 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg text-sm transition-colors"
          >
            Go Home
          </button>
        </div>
      </div>
    </div>
  );
};

export default RouteErrorFallback;
